import { Search, X } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface StatusOption<V extends string> {
  value: V
  label: string
  count: number
}

/**
 * The bar above an index table (workflows, forms, sequences, templates…), as in HubSpot: a search box
 * — essential once there are dozens or hundreds — and status tabs with counts. Filtering is the page's
 * own (these lists are loaded whole); this is only the controls.
 */
export default function IndexToolbar<V extends string>({
  query,
  onQueryChange,
  placeholder,
  statuses,
  status,
  onStatusChange,
  shown,
  total,
}: {
  query: string
  onQueryChange: (q: string) => void
  placeholder: string
  statuses?: StatusOption<V>[]
  status?: V
  onStatusChange?: (v: V) => void
  /** Rows after filtering / before — shows "12 of 265" while a filter is on. */
  shown: number
  total: number
}) {
  const filtering = query.trim() !== '' || (statuses && status !== statuses[0]?.value)
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <div className="relative flex-1 min-w-48 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="w-full pl-8 pr-8 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
        />
        {query && (
          <button type="button" onClick={() => onQueryChange('')} aria-label="Clear search" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={14} />
          </button>
        )}
      </div>
      {statuses && onStatusChange && (
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" role="group" aria-label="Status">
          {statuses.map((s) => (
            <button
              key={s.value}
              type="button"
              aria-pressed={status === s.value}
              onClick={() => onStatusChange(s.value)}
              className={cn('px-3 py-1.5 text-sm rounded-md whitespace-nowrap', status === s.value ? 'bg-brand-primary text-white' : 'text-gray-600 hover:text-brand-dark')}
            >
              {s.label} <span className={cn('tabular-nums', status === s.value ? 'text-white/80' : 'text-gray-400')}>{s.count}</span>
            </button>
          ))}
        </div>
      )}
      {filtering && <span className="text-xs text-gray-500 tabular-nums">{shown} of {total}</span>}
    </div>
  )
}

